import React, { useRef, useState } from 'react';
import { Checkbox } from 'react-bootstrap';
import { useField } from 'react-final-form';
import {
  ALL_INITIATIVE_TYPES,
  ALL_SECTIONS,
  DUPLICATION_INITIATIVE_TYPES,
  DUPLICATION_SECTIONS,
  duplicationText,
  getDuplicationWarnings,
} from '../../../constants/projectDuplication.js';
import TriStateCheckbox from '../../TriStateCheckbox.jsx';
import DownArrowIcon from '../../icons/DownArrowIcon.jsx';
import QuestionBlueIcon from '../../icons/QuestionBlueIcon.jsx';
import WhatGetsCopiedTooltip from './WhatGetsCopiedTooltip.jsx';
import styles from './DuplicateProjectModal.module.css';

const toggleValue = (values, value) =>
  values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];

// Keeps the order of `all`, so the request does not depend on the click order.
const inOrder = (all, values) => all.filter((value) => values.includes(value));

// Estimated tooltip height + gap; below this much room above the icon, the tooltip flips below it.
const MIN_SPACE_ABOVE = 320;

const initiativesSummary = (count) => {
  if (count === ALL_INITIATIVE_TYPES.length) return 'All 4 types';
  if (!count) return 'None';

  return `${count} of ${ALL_INITIATIVE_TYPES.length} types`;
};

const WhatToCopyField = () => {
  const { input: sectionsInput } = useField('sections');
  const { input: initiativeTypesInput } = useField('initiativeTypes');
  const [initiativesExpanded, setInitiativesExpanded] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPlacement, setTooltipPlacement] = useState('top');
  const tooltipIconRef = useRef(null);

  const openTooltip = () => {
    const rect = tooltipIconRef.current?.getBoundingClientRect();
    setTooltipPlacement(rect && rect.top < MIN_SPACE_ABOVE ? 'bottom' : 'top');
    setTooltipOpen(true);
  };
  const closeTooltip = () => setTooltipOpen(false);

  const sections = sectionsInput.value || [];
  const initiativeTypes = initiativeTypesInput.value || [];

  const setSections = (values) =>
    sectionsInput.onChange(inOrder(ALL_SECTIONS, values));
  const setInitiativeTypes = (values) =>
    initiativeTypesInput.onChange(inOrder(ALL_INITIATIVE_TYPES, values));

  const allSelected =
    sections.length === ALL_SECTIONS.length &&
    initiativeTypes.length === ALL_INITIATIVE_TYPES.length;
  const allInitiativeTypesSelected =
    initiativeTypes.length === ALL_INITIATIVE_TYPES.length;

  const onToggleAll = () => {
    setSections(allSelected ? [] : ALL_SECTIONS);
    setInitiativeTypes(allSelected ? [] : ALL_INITIATIVE_TYPES);
  };

  const warnings = getDuplicationWarnings({ sections, initiativeTypes });

  return (
    <div className={styles.whatToCopy}>
      <div className={`flex ${styles.whatToCopyHeader}`}>
        <div className="flex what_to_copy">
          <span className={`control-label ${styles.label}`}>
            {duplicationText('whatToCopy')}
          </span>
          <div
            className={styles.tooltipWrapper}
            onMouseEnter={openTooltip}
            onMouseLeave={closeTooltip}
            onFocus={openTooltip}
            onBlur={closeTooltip}
            onKeyDown={(e) => e.key === 'Escape' && closeTooltip()}
          >
            <button
              type="button"
              ref={tooltipIconRef}
              className={`btn-clear bg-transparent ${styles.iconButton}`}
              aria-label="What gets copied?"
              aria-describedby={
                tooltipOpen ? 'what-gets-copied-tooltip' : undefined
              }
              onClick={openTooltip}
            >
              <QuestionBlueIcon />
            </button>

            {tooltipOpen ? (
              <div
                id="what-gets-copied-tooltip"
                role="tooltip"
                className={`${styles.tooltip} ${
                  tooltipPlacement === 'top'
                    ? styles.tooltipTop
                    : styles.tooltipBottom
                }`}
              >
                <WhatGetsCopiedTooltip />
              </div>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className={`btn-clear bg-transparent text-primary ${styles.linkButton}`}
          onClick={onToggleAll}
        >
          {duplicationText(allSelected ? 'clearAll' : 'selectAll')}
        </button>
      </div>

      <div className={`what_to_copy_box ${styles.sectionsBox}`}>
        <div className={styles.grid}>
          {DUPLICATION_SECTIONS.map(({ value, label }) => (
            <Checkbox
              key={value}
              checked={sections.includes(value)}
              onChange={() => setSections(toggleValue(sections, value))}
            >
              <span className="checkmark"></span>
              <span className="text-small">{label}</span>
            </Checkbox>
          ))}
        </div>

        <div className={styles.initiatives}>
          <div className="flex">
            <TriStateCheckbox
              checked={allInitiativeTypesSelected}
              indeterminate={
                !!initiativeTypes.length && !allInitiativeTypesSelected
              }
              onChange={() =>
                setInitiativeTypes(
                  allInitiativeTypesSelected ? [] : ALL_INITIATIVE_TYPES
                )
              }
              label={duplicationText('initiatives')}
              className="text-small"
            >
              <span className={`text-muted text-small ${styles.summary}`}>
                {initiativesSummary(initiativeTypes.length)}
              </span>
            </TriStateCheckbox>
            <button
              type="button"
              className={`btn-clear bg-transparent ${styles.caret}`}
              onClick={() => setInitiativesExpanded(!initiativesExpanded)}
              aria-expanded={initiativesExpanded}
              aria-label="Initiative types"
            >
              <span
                className={`${styles.caretIcon} ${
                  initiativesExpanded ? styles.caretIconExpanded : ''
                }`}
              >
                <DownArrowIcon />
              </span>
            </button>
          </div>

          {initiativesExpanded ? (
            <div className={`${styles.grid} ${styles.initiativeTypes}`}>
              {DUPLICATION_INITIATIVE_TYPES.map(({ value, label }) => (
                <Checkbox
                  key={value}
                  checked={initiativeTypes.includes(value)}
                  onChange={() =>
                    setInitiativeTypes(toggleValue(initiativeTypes, value))
                  }
                >
                  <span className="checkmark"></span>
                  <span className="text-small">{label}</span>
                </Checkbox>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {warnings.length ? (
        <div className={styles.warnings}>
          {warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default WhatToCopyField;
